import { sharedCookieDomain } from './cookie-domain'

export const IDLE_STORAGE_KEY = 'trellis.auth.lastActivityAt'

const EXPIRED = 'Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24

export type StoredActivity =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'stamped'; readonly at: number }

const ABSENT: StoredActivity = Object.freeze({ kind: 'absent' })
const INVALID: StoredActivity = Object.freeze({ kind: 'invalid' })

let inMemoryStamp: number | null = null
let storageIsWritable = true

function storage(): Storage | null {
  try {
    return globalThis.localStorage
  } catch {
    return null
  }
}

function parse(raw: string | null): StoredActivity {
  if (raw === null) return ABSENT

  const parsed = Number(raw)
  if (raw.trim() === '' || !Number.isFinite(parsed)) return INVALID

  return { kind: 'stamped', at: parsed }
}

function readCookie(): StoredActivity {
  const doc = globalThis.document as Document | undefined
  if (!doc) return ABSENT

  try {
    for (const pair of doc.cookie.split(';')) {
      const [name, ...rest] = pair.split('=')
      if (name?.trim() === IDLE_STORAGE_KEY) return parse(rest.join('='))
    }
  } catch {
    return ABSENT
  }

  return ABSENT
}

function writeCookie(at: number): void {
  const domain = sharedCookieDomain()
  if (domain === '') return

  const doc = globalThis.document as Document | undefined
  if (!doc) return

  try {
    doc.cookie = `${IDLE_STORAGE_KEY}=${String(at)}; Path=/; Domain=${domain}; Max-Age=${String(COOKIE_MAX_AGE_SECONDS)}; Secure; SameSite=Lax`
  } catch {}
}

function clearCookie(): void {
  const doc = globalThis.document as Document | undefined
  if (!doc) return

  const domain = sharedCookieDomain()

  try {
    doc.cookie = `${IDLE_STORAGE_KEY}=; Path=/; ${EXPIRED}`
    if (domain !== '') {
      doc.cookie = `${IDLE_STORAGE_KEY}=; Path=/; Domain=${domain}; ${EXPIRED}`
    }
  } catch {}
}

function newest(...candidates: StoredActivity[]): StoredActivity {
  let best: StoredActivity = ABSENT

  for (const candidate of candidates) {
    if (candidate.kind === 'stamped') {
      if (best.kind !== 'stamped' || candidate.at > best.at) best = candidate
      continue
    }

    if (candidate.kind === 'invalid' && best.kind === 'absent') best = INVALID
  }

  return best
}

function readLocal(): StoredActivity {
  const store = storage()

  if (!store || !storageIsWritable) {
    return inMemoryStamp === null ? ABSENT : { kind: 'stamped', at: inMemoryStamp }
  }

  try {
    return parse(store.getItem(IDLE_STORAGE_KEY))
  } catch {
    return inMemoryStamp === null ? ABSENT : { kind: 'stamped', at: inMemoryStamp }
  }
}

export function readLastActivity(): StoredActivity {
  return newest(readCookie(), readLocal())
}

export function writeLastActivity(at: number): void {
  inMemoryStamp = at
  writeCookie(at)

  const store = storage()
  if (!store) return

  try {
    store.setItem(IDLE_STORAGE_KEY, String(at))
    storageIsWritable = true
  } catch {
    storageIsWritable = false
  }
}

export function clearLastActivity(): void {
  inMemoryStamp = null
  clearCookie()

  try {
    storage()?.removeItem(IDLE_STORAGE_KEY)
  } catch {}
}

export function subscribeToLastActivity(onChange: () => void): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key === null || event.key === IDLE_STORAGE_KEY) onChange()
  }

  globalThis.addEventListener('storage', handler)
  return () => {
    globalThis.removeEventListener('storage', handler)
  }
}

export function resetIdleStoreForTests(): void {
  inMemoryStamp = null
  storageIsWritable = true
}
